import L from 'leaflet';
import type { DeviceType } from '@findmy/shared';
import type { MarkerState } from '../lib/device';

// Lucide icon shapes (ISC licence), inlined because Leaflet markers take an HTML string.
// Only presentation attributes and classes are used: inline style attributes would
// break the server's Content-Security-Policy.
const svg = (body: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;

const icons: Record<'phone' | 'laptop' | 'other', string> = {
  phone: svg('<rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/>'),
  laptop: svg(
    '<path d="M18 5a2 2 0 0 1 2 2v8.526a2 2 0 0 0 .212.897l1.068 2.127a1 1 0 0 1-.9 1.45H3.62a1 1 0 0 1-.9-1.45l1.068-2.127A2 2 0 0 0 4 15.526V7a2 2 0 0 1 2-2z"/><path d="M20.054 15.987H3.946"/>',
  ),
  other: svg('<rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M12 18h.01"/>'),
};

function iconFor(type: DeviceType): string {
  if (type === 'android' || type === 'ios') return icons.phone;
  if (type === 'linux' || type === 'windows' || type === 'macos') return icons.laptop;
  return icons.other;
}

const escapeHtml = (text: string) =>
  text.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );

export function markerIcon(opts: {
  id: string;
  name: string;
  type: DeviceType;
  state: MarkerState;
  selected: boolean;
}): L.DivIcon {
  const classes = ['fm-marker', `fm-marker--${opts.state}`, opts.selected ? 'fm-marker--selected' : '']
    .filter(Boolean)
    .join(' ');
  return L.divIcon({
    className: 'fm-marker-wrap',
    html: `<div class="${classes}" data-device-id="${escapeHtml(opts.id)}" aria-label="${escapeHtml(opts.name)}">${iconFor(opts.type)}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
}

/** Accuracy-circle colours, matching the marker states. */
export const circleColour: Record<MarkerState, string> = {
  fresh: '#2563eb',
  stale: '#64748b',
  very_stale: '#dc2626',
  lost: '#f59e0b',
};
